import * as React from "react";

import { Badge, Button, Group, Stack, Text } from "@mantine/core";
import { IFuzzyTally } from "../../../types/imports/ImportPerson";
import { IPerson } from "../../../types/persons/person";
import { Role } from "../../../types/persons/Role";
import { Privilege } from "../../../types/persons/privilege";

export interface IFuzzyPersonMatchProps {
  fuzzyTally?: IFuzzyTally<IPerson> | null;
  onAssociate: () => void;
  onContinueToCreate?: () => void;
}

const formatRoles = (roles?: number) =>
  roles && roles !== 0 ? Role.getRoleNames(roles).join(", ") : undefined;

const formatPrivileges = (privileges?: number) =>
  privileges && privileges !== 0
    ? Privilege.getNames(privileges).join(", ")
    : undefined;

const FuzzyPersonMatch: React.FC<IFuzzyPersonMatchProps> = ({
  fuzzyTally,
  onAssociate,
  onContinueToCreate,
}) => {
  if (!fuzzyTally) return null;

  const person = fuzzyTally.d;
  if (!person) return null;

  const activeAssociations = (person.associations || []).filter(
    (assoc) => !assoc.finish,
  );
  const flattenedRoles = activeAssociations.reduce(
    (prev, assoc) => prev | assoc.role,
    0,
  );
  const flattenedPrivileges = activeAssociations.reduce(
    (prev, assoc) => prev | assoc.privilege,
    0,
  );

  const contactItems = [
    person.email && `Email · ${person.email}`,
    person.mobile && `Mobile · ${person.mobile}`,
  ].filter(Boolean) as string[];

  const displayName = person.fullName || `${person.firstName} ${person.lastName}`;

  return (
    <Stack gap="xs" p="xs">
      <Group gap="xs">
        <Text fw={600} size="sm">
          Possible duplicate found
        </Text>
        <Badge color="yellow" variant="light" size="sm">
          Review
        </Badge>
      </Group>
      <Text size="sm" fw={500}>
        {displayName}
        {person.preferredName &&
          person.preferredName !== person.firstName && (
            <Text span size="sm" c="dimmed">
              {" "}({person.preferredName})
            </Text>
          )}
      </Text>
      {contactItems.length > 0 && (
        <Group gap="sm">
          {contactItems.map((item) => (
            <Badge key={item} color="gray" variant="light" radius="sm">
              {item}
            </Badge>
          ))}
        </Group>
      )}
      {(flattenedRoles || flattenedPrivileges || activeAssociations.length > 0) && (
        <Stack gap={2}>
          {flattenedRoles ? (
            <Text size="xs" c="dimmed">
              Roles: {formatRoles(flattenedRoles)}
            </Text>
          ) : null}
          {flattenedPrivileges ? (
            <Text size="xs" c="dimmed">
              Privileges: {formatPrivileges(flattenedPrivileges)}
            </Text>
          ) : null}
          {activeAssociations.length > 0 ? (
            <Text size="xs" c="dimmed">
              Active associations: {activeAssociations.length}
            </Text>
          ) : null}
        </Stack>
      )}
      <Text size="xs" c="dimmed">
        Matches on: {fuzzyTally.matches.join(", ")}
      </Text>
      <Button
        color="blue"
        variant="light"
        size="xs"
        onClick={onAssociate}
      >
        Associate this person
      </Button>
      {onContinueToCreate ? (
        <Button
          color="gray"
          variant="subtle"
          size="xs"
          onClick={onContinueToCreate}
        >
          Create a separate person
        </Button>
      ) : (
        <Text size="xs" c="dimmed">
          Not them? Keep completing the form to create someone new.
        </Text>
      )}
    </Stack>
  );
};

export default FuzzyPersonMatch;
